import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import '../../styles/tsumugi-design.css';
import '../../styles/pages/AffiliateAdmin.css';

const getAdminHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return { 'X-Admin-Token': token };
};

const EmpathyFeedAdmin = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState({ published: [], unpublished: [], drafts: [] });
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  // フォームの初期値
  const emptyForm = {
    author_name: '',
    author_age: '',
    content: '',
    tsumugi_comment: '',
    category: '疲労',
    is_published: false,
    display_order: 0
  };

  const [formData, setFormData] = useState(emptyForm);

  const categories = [
    '疲労',
    '仕事ストレス',
    '食事の悩み',
    '体重・外見',
    '孤独感',
    'その他'
  ];

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/api/empathy-feed/admin/posts', {
        headers: getAdminHeaders()
      });

      if (response.data.success) {
        setPosts({
          published: response.data.published,
          unpublished: response.data.unpublished,
          drafts: response.data.drafts
        });
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      alert('投稿の取得に失敗しました');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingPost) {
        // 編集
        await api.put(
          `/api/empathy-feed/admin/posts/${editingPost.id}`,
          formData,
          { headers: getAdminHeaders() }
        );
        alert('投稿を更新しました');
      } else {
        // 新規作成
        await api.post(
          '/api/empathy-feed/admin/posts',
          formData,
          { headers: getAdminHeaders() }
        );
        alert(formData.is_published ? '投稿を公開しました' : '下書きを保存しました');
      }

      setFormData(emptyForm);
      setEditingPost(null);
      setShowForm(false);
      fetchPosts();
    } catch (error) {
      console.error('Failed to save post:', error);
      alert('保存に失敗しました');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      author_name: post.author_name,
      author_age: post.author_age || '',
      content: post.content,
      tsumugi_comment: post.tsumugi_comment || '',
      category: post.category || '疲労',
      is_published: post.is_published,
      display_order: post.display_order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('本当に削除しますか？')) return;

    try {
      await api.delete(`/api/empathy-feed/admin/posts/${postId}`, {
        headers: getAdminHeaders()
      });
      alert('投稿を削除しました');
      fetchPosts();
    } catch (error) {
      console.error('Failed to delete post:', error);
      alert('削除に失敗しました');
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await api.put(
        `/api/empathy-feed/admin/posts/${post.id}`,
        { is_published: !post.is_published },
        { headers: getAdminHeaders() }
      );
      alert(post.is_published ? '非公開にしました' : '公開しました');
      fetchPosts();
    } catch (error) {
      console.error('Failed to toggle publish:', error);
      alert('操作に失敗しました');
    }
  };

  return (
    <div className="admin-layout">
      <div className="empathy-admin">
        <div className="admin-page-header">
          <button
            onClick={() => navigate('/admin')}
            className="btn-back"
          >
            ← 管理ダッシュボード
          </button>
          <h1 className="admin-page-title">共感フィード管理</h1>
        </div>

        <div className="empathy-admin__actions">
          <button
            className="btn-primary"
            onClick={() => {
              setShowForm(!showForm);
              setEditingPost(null);
              setFormData(emptyForm);
            }}
          >
            {showForm ? '閉じる' : '+ 新規投稿'}
          </button>
        </div>

      {/* 投稿フォーム */}
      {showForm && (
        <div className="tsumugi-card empathy-admin__form-card">
          <h2 className="tsumugi-h2 mb-5">
            {editingPost ? '投稿を編集' : '新規投稿作成'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="tsumugi-label">投稿者名 *</label>
              <input
                type="text"
                className="tsumugi-input"
                placeholder="例: 27歳 Bさん、30代女性"
                value={formData.author_name}
                onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                required
              />
            </div>

            <div className="mb-4">
              <label className="tsumugi-label">カテゴリ</label>
              <select
                className="tsumugi-input"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="tsumugi-label">投稿本文 * （200文字以内推奨）</label>
              <textarea
                className="tsumugi-input"
                rows="4"
                placeholder="例: 今日も午後、眠気で辛い。&#10;でも頑張ってる自分を認めよう。"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
                maxLength="200"
              />
              <small className="tsumugi-caption">{formData.content.length}/200文字</small>
            </div>

            <div className="mb-4">
              <label className="tsumugi-label">つむぎのコメント（150文字以内推奨）</label>
              <textarea
                className="tsumugi-input"
                rows="3"
                placeholder="例: そうだよ。橋本病だと、午後の眠気は本当に辛いよね。&#10;あなたは頑張ってるよ。"
                value={formData.tsumugi_comment}
                onChange={(e) => setFormData({ ...formData, tsumugi_comment: e.target.value })}
                maxLength="150"
              />
              <small className="tsumugi-caption">{formData.tsumugi_comment.length}/150文字</small>
            </div>

            <div className="mb-4 flex gap-5 items-center">
              <label className="empathy-admin__checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                />
                <span className="tsumugi-label">すぐに公開</span>
              </label>

              <div className="empathy-admin__display-order">
                <label className="tsumugi-label">表示順序</label>
                <input
                  type="number"
                  className="tsumugi-input empathy-admin__display-order-input"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                />
                <small className="tsumugi-caption">大きい数字ほど上に表示</small>
              </div>
            </div>

            <div className="empathy-admin__form-buttons">
              <button type="submit" className="tsumugi-btn tsumugi-btn-primary">
                {editingPost ? '更新する' : (formData.is_published ? '公開する' : '下書き保存')}
              </button>
              <button
                type="button"
                className="tsumugi-btn tsumugi-btn-outline"
                onClick={() => {
                  setShowForm(false);
                  setEditingPost(null);
                  setFormData(emptyForm);
                }}
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 公開中の投稿 */}
      <div className="empathy-admin__section">
        <h2 className="tsumugi-h2 mb-4">
          📊 公開中の投稿 ({posts.published.length}件)
        </h2>
        {posts.published.map(post => (
          <PostCard
            key={post.id}
            post={post}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onTogglePublish={handleTogglePublish}
          />
        ))}
      </div>

      {/* 下書き・非公開 */}
      <div className="empathy-admin__section">
        <h2 className="tsumugi-h2 mb-4">
          📝 下書き・非公開 ({posts.unpublished.length + posts.drafts.length}件)
        </h2>
        {posts.unpublished.map(post => (
          <PostCard
            key={post.id}
            post={post}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onTogglePublish={handleTogglePublish}
          />
        ))}
      </div>
      </div>
    </div>
  );
};

const PostCard = ({ post, onEdit, onDelete, onTogglePublish }) => {
  return (
    <div
      className={`tsumugi-card empathy-admin__post-card ${
        post.is_published
          ? 'empathy-admin__post-card--published'
          : 'empathy-admin__post-card--unpublished'
      }`}
    >
      <div className="empathy-admin__post-header">
        <div className="empathy-admin__post-content">
          <div className="empathy-admin__post-meta mb-2">
            <span className="tsumugi-tag tsumugi-tag-lilac">{post.category || 'カテゴリなし'}</span>
            <span className="tsumugi-body font-medium">{post.author_name}</span>
            {post.is_published && (
              <span className="empathy-admin__post-status">● 公開中</span>
            )}
          </div>
          <p className="tsumugi-body mb-2 empathy-admin__post-text">
            "{post.content}"
          </p>
          {post.tsumugi_comment && (
            <p className="tsumugi-body mb-2 empathy-admin__post-tsumugi">
              つむぎ: {post.tsumugi_comment}
            </p>
          )}
          <div className="empathy-admin__post-stats">
            <span>❤️ {post.empathy_count} わかる</span>
            <span>📅 {new Date(post.created_at).toLocaleString('ja-JP')}</span>
            <span>順序: {post.display_order}</span>
          </div>
        </div>
        <div className="empathy-admin__post-actions">
          <button
            className="tsumugi-btn tsumugi-btn-outline empathy-admin__action-btn"
            onClick={() => onEdit(post)}
          >
            編集
          </button>
          <button
            className="tsumugi-btn tsumugi-btn-outline empathy-admin__action-btn"
            onClick={() => onTogglePublish(post)}
          >
            {post.is_published ? '非公開' : '公開'}
          </button>
          <button
            className="tsumugi-btn tsumugi-btn-outline empathy-admin__action-btn empathy-admin__action-btn--delete"
            onClick={() => onDelete(post.id)}
          >
            削除
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmpathyFeedAdmin;
